import React, { useContext, useMemo } from 'react';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import PropTypes from 'prop-types';
import { Optional, Box, Icon, IconWrapper, Copy, H6, Button } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import { VinContext } from '../../contexts/VinContext';
import themeStyles from './styles/NoticeCard.styles';
import NoticeUpdate from './NoticeUpdate';
import useConfiguration from '../../hooks/useConfiguration';
import componentConfig from './componentConfig';
import pushGtmNoticeClickEvent from '../../utils/gtm/pushGtmNoticeClickEvent';
import getPdfAbsoluteUrl from '../../utils/getPdfAbsoluteUrl';

const CardContainer = themeStyles.apply(Box, 'CardContainer');
const HeadingContainer = themeStyles.apply(Box, 'HeadingContainer');
const UpdatesContainer = themeStyles.apply(Box, 'UpdatesContainer');
const Description = themeStyles.apply(Copy, 'Description');
const LinkButton = themeStyles.apply(Button, 'LinkButton');

const NoticeCard = ({
  title,
  description,
  status,
  noticeLink,
  pdfLink,
  servicedOn,
  isPDF,
  isPartsAvailable,
  noticeLinkText,
  noticeUpdates,
  ...styledProps
}) => {
  const { viewNoticeColor, noticeCardBackgroundColor } = useConfiguration(componentConfig);
  const vin = useContext(VinContext);
  const { t } = useTranslation();
  const { sitecoreContext } = useSitecoreContext();
  const hondaImageHost = sitecoreContext?.hondaImageHost;
  const recallAssetContext = sitecoreContext?.recallAssetContext;
  const pdfUrl = useMemo(
    () => getPdfAbsoluteUrl(pdfLink, { hondaImageHost, recallAssetContext }),
    [pdfLink, hondaImageHost, recallAssetContext],
  );

  // ToDo: check defaultNoticeUrl dictionary value with sitecoreContext/Sub-apps.Recalls.campaignNoticeUrl
  const defaultNoticeLink = t('Sub-apps.Recalls.campaignNoticeUrl');
  const noticeRefLink = isPDF ? pdfUrl : noticeLink || defaultNoticeLink;
  const handleClick = () => pushGtmNoticeClickEvent(vin, title);

  return (
    <CardContainer {...styledProps} noticeCardBackgroundColor={noticeCardBackgroundColor} data-testid="cy-recall-card">
      <HeadingContainer data-testid="cy-noticeCard-title">
        <H6 fontFamily="bold">{title}</H6>
        <Optional when={isPDF}>
          <IconWrapper size="iconWrapper.sm" alignSelf="start">
            <Icon name="pdf" title={t('pdfAvailable')} />
          </IconWrapper>
        </Optional>
      </HeadingContainer>
      <Description data-testid="cy-noticeCard-description">{description}</Description>
      <Box>
        <UpdatesContainer data-testid="cy-updates-container">
          <NoticeUpdate
            status={status}
            serviceText={t('Sub-apps.Recalls.serviceCompletedOnLabel')}
            servicedOn={servicedOn}
            noticeUpdates={noticeUpdates}
            isPartsAvailable={isPartsAvailable}
          />
        </UpdatesContainer>
        <Box data-testid="cy-notice-link">
          <LinkButton
            styling="tertiary"
            as="a"
            href={noticeRefLink}
            target="_blank"
            viewNoticeColor={viewNoticeColor}
            onClick={handleClick}
          >
            {noticeLinkText}
          </LinkButton>
          <Icon ml="xs" height="10px" name="arrowRight" iconColor="primary" title={t('linkToNotice')} />
        </Box>
      </Box>
    </CardContainer>
  );
};

NoticeCard.propTypes = {
  /**
   * Title of campaign
   */
  title: PropTypes.string,
  /**
   * Description of the campaign
   */
  description: PropTypes.string.isRequired,
  /**
   * Link to the campaign
   */
  noticeLink: PropTypes.string,
  /**
   * Date of when service was completed
   */
  servicedOn: PropTypes.string,
  /**
   * Whether there is a PDF to the campaign
   */
  pdfLink: PropTypes.string,
  /**
   * Notice Link label
   */
  noticeLinkText: PropTypes.string,
  /**
   * Notice Updates text
   */
  noticeUpdates: PropTypes.string,
  /**
   * Identifier if url is PDF
   */
  isPDF: PropTypes.bool,
  /**
   * parts availability
   */
  isPartsAvailable: PropTypes.bool,
  /**
   * campaign status: 'ongoing' or 'completed'
   */
  status: PropTypes.string,
};

export default NoticeCard;
